import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, IconButton, Button,
    Avatar, Divider, CircularProgress, Grow, Dialog,
    DialogTitle, DialogContent, DialogActions, Tooltip, Stack, Popover,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip,
    Select, MenuItem, Fade
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import GridOnIcon from '@mui/icons-material/GridOn';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import FolderSpecialRoundedIcon from '@mui/icons-material/FolderSpecialRounded';
import axios from 'axios';
import { marked } from 'marked';
import API_URL from '../../config';
import Loading from '../../components/Loading';

// Colorful & Friendly Meta Types (Matches previous page)
const TYPE_META = {
    pdf: { label: 'PDF', color: '#FF4757', bg: '#FFE9EB', icon: <PictureAsPdfIcon sx={{ fontSize: 16 }} /> },
    word: { label: 'Word', color: '#2a5298', bg: '#e8eef7', icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
    map_pdf: { label: 'Map', color: '#2ED573', bg: '#E8FAEE', icon: <MapIcon sx={{ fontSize: 16 }} /> },
    excel: { label: 'Excel', color: '#FFA502', bg: '#FFF5E5', icon: <GridOnIcon sx={{ fontSize: 16 }} /> },
};

const WORK_STATUSES = ['New', 'In Progress', 'Completed', 'Cancelled'];

const BOQ_COLUMNS = ['category', 'item', 'description', 'qty', 'unit', 'rate', 'amount_qar', 'item_no', 'quantity', 'amount'];

// Reorder a list of column keys to follow the canonical BOQ_COLUMNS order,
// keeping any unknown columns (in their original order) appended at the end.
const orderColumns = (cols) => {
    if (!Array.isArray(cols) || cols.length === 0) return cols;
    return [
        ...BOQ_COLUMNS.filter(k => cols.includes(k)),
        ...cols.filter(k => !BOQ_COLUMNS.includes(k))
    ];
};

const STATUS_STYLE = {
    'New': { color: '#2a5298', bg: '#e8eef7' },
    'In Progress': { color: '#FFA502', bg: '#FFF5E5' },
    'Completed': { color: '#2ED573', bg: '#E8FAEE' },
    'Cancelled': { color: '#FF4757', bg: '#FFE9EB' },
};

function TenderingChat() {
    const { guid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const passedSessionName = location.state?.sessionName;
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([{ sender: 'bot', text: 'Hello! Ask me anything about the uploaded tender documents. ✨' }]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [fileFilter, setFileFilter] = useState('all');
    const [filesAnchorEl, setFilesAnchorEl] = useState(null);
    const [popoverFiles, setPopoverFiles] = useState([]);
    const [boqOpen, setBoqOpen] = useState(false);
    const [boqRows, setBoqRows] = useState([]);
    const [boqColumns, setBoqColumns] = useState([]);
    const [boqColumnLabels, setBoqColumnLabels] = useState(null);
    const [boqTemplateId, setBoqTemplateId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [boqMsgIndex, setBoqMsgIndex] = useState(null);
    const [boqTemplateType, setBoqTemplateType] = useState(null);
    const [boqRawData, setBoqRawData] = useState(null);
    const [boqRowsMeta, setBoqRowsMeta] = useState(null);
    const [boqSaving, setBoqSaving] = useState(false);
    const [boqExporting, setBoqExporting] = useState(false);
    const chatEndRef = useRef(null);
    const loadedRef = useRef(false);
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    useEffect(() => {
        const load = async () => {
            if (loadedRef.current === guid) return;
            loadedRef.current = guid;
            try {
                const [sesRes, histRes] = await Promise.all([
                    axios.get(`${API_URL}/tendering/sessions/${guid}`, { headers }),
                    axios.get(`${API_URL}/tendering/session_history`, { params: { session_id: guid }, headers })
                ]);
                setSession(sesRes.data);

                const histData = histRes.data || {};
                const historyList = Array.isArray(histData.history) ? histData.history : [];
                if (historyList.length > 0) {
                    const loadedMessages = historyList.map((m) => {
                        const boqType = String(m.boq_type || 'standard').toLowerCase();
                        const isClientType = boqType.includes('client');
                        const rawTable = m.boq_table ?? null;
                        const normalized = rawTable
                            ? (isClientType ? normalizeClientBoq(rawTable) : normalizeBoq(rawTable))
                            : { columns: [], rows: [], labels: null };
                        
                        const boqRawData = (isClientType && rawTable && typeof rawTable === 'object' && !Array.isArray(rawTable))
                            ? rawTable : null;
                        
                        return {
                            sender: m.role === 'user' ? 'user' : 'bot',
                            text: m.content || '',
                            boqTable: normalized.rows || [],
                            boqColumns: normalized.columns || [],
                            boqColumnLabels: normalized.labels || null,
                            boqId: null,
                            templateType: m.boq_type || 'standard',
                            hasBoq: Boolean(rawTable) && (normalized.rows || []).length > 0,
                            boqRawData,
                            boqTables: Array.isArray(histData.boq_tables) ? histData.boq_tables : null
                        };
                    });
                    setMessages(loadedMessages);
                    setTemplates(loadedMessages.filter(m => m.hasBoq).map((m, index) => {
                        const raw = (m.boqRawData && m.templateType && String(m.templateType).toLowerCase().includes('client'))
                            ? m.boqRawData : undefined;
                        return {
                            id: index + 1, type: m.templateType || 'standard',
                            sig: JSON.stringify({ type: m.templateType || 'standard', cols: m.boqColumns, rows: m.boqTable.length }),
                            columns: m.boqColumns, rows: m.boqTable, labels: m.boqColumnLabels, raw
                        };
                    }));
                }
            } catch (err) {
                console.error('Error loading tendering session:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [guid]);

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, chatLoading]);

    const handleSendMessage = async () => {
        if (!chatInput.trim() || chatLoading) return;
        const userMessage = chatInput.trim();
        setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
        setChatInput('');
        setChatLoading(true);
        setMessages(prev => [...prev, { sender: 'bot', text: 'Thinking... ✨', isLoader: true }]);
        try {
            const res = await axios.post(`${API_URL}/tendering/chat`,
                { session_id: guid, message: userMessage },
                { headers: { ...headers, 'Content-Type': 'application/json' }, timeout: 600000 }
            );
            const data = res.data || {};
            const boqTypeRaw = data.boq_type || data.template_type || 'standard';
            const boqType = String(boqTypeRaw).toLowerCase();
            const hasClientTable = Array.isArray(data.boq_table_client_template)
                ? data.boq_table_client_template.length > 0
                : (data.boq_table_client_template && typeof data.boq_table_client_template === 'object' && Object.keys(data.boq_table_client_template).length > 0);
            const isClient = boqType.includes('client') || hasClientTable;
            const rawBoq = isClient
                ? (data.boq_table_client_template ?? data.boq_table)
                : (data.boq_table_static_template ?? data.boq_table);

            const staticRaw = data.boq_table_static_template;
            const clientRaw = data.boq_table_client_template;
            const rawClientData = clientRaw;

            const fallback = isClient
                ? normalizeClientBoq(rawBoq, data.boq_columns, data.boq_rows)
                : normalizeBoq(rawBoq);
            const finalRows = fallback.rows;
            const finalCols = fallback.columns;

            const newMessage = {
                sender: 'bot',
                text: data.message || data.error || 'No response.',
                boqTable: finalRows, boqColumns: finalCols, boqColumnLabels: fallback.labels || null,
                boqId: data.boq_id || null, templateType: boqType,
                hasBoq: finalRows.length > 0, boqRawData: (isClient && rawClientData) ? rawClientData : null,
                boqTables: data.boq_tables || null
            };
            setMessages(prev => {
                const base = [...prev];
                base[base.length - 1] = newMessage;
                return base;
            });

            const register = (table, type) => {
                if (!table) return false;
                const n = type === 'client'
                    ? normalizeClientBoq(table, isClient ? data.boq_columns : [], isClient ? data.boq_rows : [])
                    : normalizeBoq(table);
                if (!n.columns.length && !n.rows.length) return false;
                setTemplates(prev => {
                    const typedSig = JSON.stringify({ type, cols: n.columns, rows: n.rows.length });
                    if (prev.some(t => t.sig === typedSig)) return prev;
                    return [...prev, { id: prev.length + 1, type, sig: typedSig, columns: n.columns, rows: n.rows, labels: n.labels || null, raw: table }];
                });
                return true;
            };
            if (isClient) {
                register(clientRaw, 'client');
                if (staticRaw) register(staticRaw, 'static');
            } else {
                register(staticRaw, 'static');
                if (clientRaw) register(clientRaw, 'client');
            }
        } catch (error) {
            console.error('Chat error:', error);
            const msg = error.response?.data?.error || error.message || 'Sorry, there was an error processing your request.';
            setMessages(prev => { const base = [...prev]; base[base.length - 1] = { sender: 'bot', text: msg }; return base; });
        } finally {
            setChatLoading(false);
        }
    };

    const fileTypeMeta = (ftype) => TYPE_META[ftype] || { label: 'File', color: '#636E72', bg: '#DFE6E9', icon: <DescriptionIcon sx={{ fontSize: 16 }} /> };

    const closePreview = () => {
        setPreviewFile(null);
        if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    };

    const fetchFileBlob = async (file) => {
        const res = await axios.get(`${API_URL}/tendering/sessions/${guid}/files/${file.id}/download`, { headers, responseType: 'blob' });
        return res.data;
    };

    const handlePreview = async (file) => {
        if (file.file_type === 'word' || file.file_type === 'excel') {
            alert('Preview is available for PDF files only. Please use Download for Word/Excel files.');
            return;
        }
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewFile(file);
        } catch (err) {
            alert('Could not load preview: ' + (err.message || err));
        }
    };

    const handleDownload = async (file) => {
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.file_name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            alert('Download failed: ' + (err.message || err));
        }
    };

    const openFilesPopover = (event, type) => {
        const list = type === 'all' ? (session?.files || []) : (session?.files || []).filter(f => f.file_type === type);
        setPopoverFiles(list);
        setFileFilter(type);
        setFilesAnchorEl(event.currentTarget);
    };

    const closeFilesPopover = () => setFilesAnchorEl(null);

    const handleWorkStatusChange = async (newStatus) => {
        const prev = session?.work_status;
        setSession(s => ({ ...s, work_status: newStatus }));
        try {
            await axios.put(`${API_URL}/tendering/sessions/${guid}/work-status`,
                { work_status: newStatus },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
        } catch (err) {
            console.error('Update status error:', err);
            setSession(s => ({ ...s, work_status: prev }));
            alert('Could not update status: ' + (err.response?.data?.error || err.message));
        }
    };

    // BOQ Parsing (Untouched Logic)
    const parseClientColumn = (column) => {
        let metadata = column;
        if (typeof metadata === 'string') {
            try { metadata = JSON.parse(metadata); } catch {
                const fields = {};
                const pattern = /(?:^|[,{]\s*)['"]?(header_text|meaning|letter|key|label)['"]?\s*:\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)'|([^,}]+))/g;
                let match;
                while ((match = pattern.exec(metadata)) !== null) {
                    fields[match[1]] = (match[2] ?? match[3] ?? match[4] ?? '').trim().replace(/\\(['"\\])/g, '$1');
                }
                metadata = Object.keys(fields).length ? fields : null;
            }
        }
        if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;

        const key = metadata.meaning === 'other' ? `other_${metadata.letter}` : (metadata.meaning || metadata.key || metadata.letter);
        if (!key) return null;
        const header = metadata.header_text;
        const label = typeof header === 'string' ? header : (header?.label || header?.text || header?.name || metadata.label || metadata.meaning || key);
        const aliases = [key, metadata.meaning, metadata.letter, metadata.key, label].filter(value => value !== null && value !== undefined).map(String);
        return { key: String(key), label: String(label), aliases };
    };

    const normalizeBoq = (table) => {
        if (!table) return { columns: [], rows: [], labels: null };
        const parseJson = (value) => {
            if (typeof value !== 'string') return value;
            try { return JSON.parse(value); } catch { return value; }
        };
        const cellText = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        };
        table = parseJson(table);

        if (!Array.isArray(table) && typeof table === 'object') {
            const headersFromTable = parseJson(table.headers);
            const columnsFromTable = parseJson(table.columns);
            const headersPart = Array.isArray(headersFromTable) && headersFromTable.length ? headersFromTable : columnsFromTable;
            const colsObj = headersPart;
            const clientColumnMetadata = Array.isArray(colsObj) ? colsObj.map(parseJson) : [];
            
            if (clientColumnMetadata.length > 0 && typeof clientColumnMetadata[0] === 'object' && !Array.isArray(clientColumnMetadata[0])) {
                const rawRows = parseJson(table.rows);
                const clientRows = Array.isArray(rawRows) ? rawRows : [];
                const colInfo = clientColumnMetadata.map(c => {
                    const meaning = c?.meaning;
                    const letter = c?.letter;
                    const header = parseJson(c?.header_text || c?.header || c?.label);
                    const key = c?.key;
                    const canonical = (meaning && meaning !== 'other') ? meaning : (letter ? `other_${letter}` : (key || meaning || 'Column'));
                    const aliases = [];
                    [canonical, meaning, header, letter, key].forEach(cand => {
                        if (cand !== null && cand !== undefined) {
                            const s = typeof cand === 'object' ? String(cand.key || cand.name || cand.label || cand.text || '') : String(cand);
                            if (!aliases.includes(s)) aliases.push(s);
                        }
                    });
                    const label = typeof header === 'object' ? (header.label || header.text || header.name || header.key) : header;
                    return { canonical: String(canonical), aliases, label: String(label || c?.label || meaning || key || letter || canonical) };
                });
                const keys = colInfo.map(ci => ci.canonical);
                const labels = {};
                colInfo.forEach(ci => { labels[ci.canonical] = ci.label; });

                const rows = clientRows.map(r => {
                    const o = {};
                    if (Array.isArray(r)) {
                        colInfo.forEach((ci, index) => { o[ci.canonical] = cellText(r[index]); });
                    } else if (r && typeof r === 'object') {
                        const rowKeys = Object.keys(r);
                        const normalizedRowKeys = new Map(rowKeys.map(key => [key.toLowerCase().replace(/[^a-z0-9]/g, ''), key]));
                        const hasMatchingKey = colInfo.some(ci => ci.aliases.some(alias => (normalizedRowKeys.has(alias.toLowerCase().replace(/[^a-z0-9]/g, '')))));
                        colInfo.forEach((ci, index) => {
                            let val = '';
                            for (const a of ci.aliases) {
                                if (a && r[a] !== undefined) { val = r[a]; break; }
                                const normalizedKey = a.toLowerCase().replace(/[^a-z0-9]/g, '');
                                const matchingKey = normalizedRowKeys.get(normalizedKey);
                                if (matchingKey !== undefined) { val = r[matchingKey]; break; }
                            }
                            if ((val === '' || val === undefined || val === null) && !hasMatchingKey) {
                                val = r[rowKeys[index]];
                            }
                            o[ci.canonical] = cellText(val);
                        });
                    } else {
                        keys.forEach(k => { o[k] = ''; });
                    }
                    return o;
                });

                const anyValue = rows.some(row => Object.values(row).some(v => v !== ''));
                if (!anyValue && clientRows.length && clientRows[0] && typeof clientRows[0] === 'object' && !Array.isArray(clientRows[0])) {
                    const rawKeys = Object.keys(clientRows[0]);
                    return { columns: rawKeys.map(k => String(k)), rows: clientRows.map(r => { const o = {}; rawKeys.forEach(k => { o[k] = cellText(r?.[k]); }); return o; }), labels: null };
                }
                return { columns: keys, rows, labels };
            }

            const rowsPart = parseJson(table.rows || table.data);
            if (Array.isArray(rowsPart)) {
                if (Array.isArray(headersPart) && headersPart.length > 0 && typeof headersPart[0] !== 'object') {
                    const cols = headersPart.map(h => String(h));
                    const rows = rowsPart.map(r => { const o = {}; cols.forEach((c, index) => { o[c] = cellText(Array.isArray(r) ? r[index] : r?.[c]); }); return o; });
                    return { columns: cols, rows, labels: null };
                }
                return normalizeBoq(rowsPart);
            }
            const listKeys = Object.keys(table).filter(k => Array.isArray(table[k]));
            if (listKeys.length > 0) {
                const len = Math.max(...listKeys.map(k => table[k].length));
                const clientColumns = listKeys.map(key => parseClientColumn(key));
                const isClientColumns = clientColumns.every(Boolean);
                const columns = isClientColumns ? clientColumns.map(column => column.key) : listKeys;
                const labels = isClientColumns ? Object.fromEntries(clientColumns.map(column => [column.key, column.label])) : null;
                const rows = [];
                for (let i = 0; i < len; i++) {
                    const o = {};
                    listKeys.forEach((key, index) => { const column = isClientColumns ? clientColumns[index].key : key; o[column] = cellText(table[key][i]); });
                    rows.push(o);
                }
                return { columns, rows, labels };
            }
            const cols = Object.keys(table);
            const o = {};
            cols.forEach(c => { o[c] = cellText(table[c]); });
            return { columns: cols, rows: [o], labels: null };
        }

        if (!Array.isArray(table) || table.length === 0) return { columns: [], rows: [], labels: null };
        if (typeof table[0] === 'object' && !Array.isArray(table[0]) && table[0] !== null) {
            const cols = [];
            table.forEach(r => Object.keys(r || {}).forEach(k => { if (!cols.includes(k)) cols.push(k); }));
            const clientColumns = cols.map(key => parseClientColumn(key));
            const isClientColumns = clientColumns.every(Boolean);
            const orderedSourceCols = isClientColumns ? cols : [...BOQ_COLUMNS.filter(k => cols.includes(k)), ...cols.filter(k => !BOQ_COLUMNS.includes(k))];
            const orderedCols = isClientColumns ? clientColumns.map(column => column.key) : orderedSourceCols;
            const labels = isClientColumns ? Object.fromEntries(clientColumns.map(column => [column.key, column.label])) : null;
            const rows = table.map(r => {
                const obj = {};
                orderedSourceCols.forEach((sourceColumn, index) => {
                    const column = isClientColumns ? clientColumns[index].key : sourceColumn;
                    obj[column] = cellText(r?.[sourceColumn]);
                });
                return obj;
            });
            return { columns: orderedCols, rows, labels };
        }
        if (Array.isArray(table[0])) {
            const cols = table[0].map((c, i) => (c === null || c === undefined || String(c).trim() === '') ? `Column ${i + 1}` : String(c));
            const rows = table.slice(1).map(arr => { const obj = {}; cols.forEach((c, i) => { obj[c] = cellText(arr?.[i]); }); return obj; });
            return { columns: cols, rows, labels: null };
        }
        return { columns: ['Value'], rows: table.map(v => ({ Value: String(v) })), labels: null };
    };

    const normalizeClientBoq = (table, fallbackColumns = [], fallbackRows = []) => {
        if (typeof table === 'string') {
            try { table = JSON.parse(table); } catch { return { columns: [], rows: [], labels: null }; }
        }
        if (!table || typeof table !== 'object' || Array.isArray(table)) return { columns: [], rows: [], labels: null };

        let clientColumns = table.columns;
        if (typeof clientColumns === 'string') {
            try { clientColumns = JSON.parse(clientColumns); } catch { return { columns: [], rows: [], labels: null }; }
        }
        if (!Array.isArray(clientColumns)) return { columns: [], rows: [], labels: null };

        const metadata = clientColumns.map(parseClientColumn);
        const hasMetadata = metadata.length > 0 && metadata.every(Boolean);
        const hasNormalizedKeys = clientColumns.length > 0 && clientColumns.every(column => (typeof column === 'string' && !column.trim().startsWith('{')));
        if (!hasMetadata && !hasNormalizedKeys) return { columns: [], rows: [], labels: null };
        
        const columns = hasMetadata ? metadata.map(column => column.key) : clientColumns;
        const labels = {};
        if (hasMetadata) { metadata.forEach(column => { labels[column.key] = column.label; }); } else { columns.forEach(column => { labels[column] = column; }); }

        const rawRows = Array.isArray(table.rows) ? table.rows : (Array.isArray(table.data) ? table.data : []);
        const parseRow = row => {
            if (typeof row !== 'string') return row;
            try { return JSON.parse(row); } catch {
                const parsed = {};
                const pattern = /(?:^|[,{]\s*)['"]([^'"]+)['"]\s*:\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)'|([^,}]+))/g;
                let match;
                while ((match = pattern.exec(row)) !== null) {
                    parsed[match[1]] = (match[2] ?? match[3] ?? match[4] ?? '').trim().replace(/\\(['"\\])/g, '$1');
                }
                return Object.keys(parsed).length ? parsed : row;
            }
        };
        const normalizedFallbackRows = Array.isArray(fallbackRows) ? fallbackRows.map(parseRow) : [];
        const rows = rawRows.map((rawRow, rowIndex) => {
            const row = parseRow(rawRow);
            const fallbackRow = parseRow(normalizedFallbackRows[rowIndex]);
            const normalizedRow = {};
            columns.forEach((column, index) => {
                const columnMetadata = hasMetadata ? metadata[index] : { key: column, label: column, aliases: [column] };
                const readByAlias = candidate => {
                    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return undefined;
                    for (const alias of columnMetadata.aliases) { if (candidate[alias] !== undefined) return candidate[alias]; }
                    const normalizedAliases = new Set(columnMetadata.aliases.map(alias => alias.toLowerCase().replace(/[^a-z0-9]/g, '')));
                    const matchingKey = Object.keys(candidate).find(key => normalizedAliases.has(key.toLowerCase().replace(/[^a-z0-9]/g, '')));
                    return matchingKey === undefined ? undefined : candidate[matchingKey];
                };
                const rowValues = row?.values || row?.cells || row?.data || row?.row || row;
                let value = Array.isArray(rowValues) ? rowValues[index] : readByAlias(rowValues);
                if ((value === undefined || value === null || value === '') && fallbackColumns[index]) {
                    const fallbackValue = Array.isArray(fallbackRow) ? fallbackRow[index] : fallbackRow?.[fallbackColumns[index]];
                    value = fallbackValue;
                    if (value === undefined || value === null || value === '') value = readByAlias(fallbackRow);
                }
                if ((value === undefined || value === null || value === '') && row && typeof row === 'object' && !Array.isArray(row)) {
                    const dataKeys = Object.keys(row).filter(key => !['template_row', 'origin', 'source', 'id'].includes(key));
                    if (dataKeys.length === columns.length) { value = row[dataKeys[index]]; }
                }
                normalizedRow[column] = value === null || value === undefined ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
            });
            return normalizedRow;
        });
        return { columns, rows, labels };
    };

    const extractRowMeta = (rawData) => {
        if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) return null;
        const rawRows = Array.isArray(rawData.rows) ? rawData.rows : [];
        return rawRows.map(r => {
            if (r && typeof r === 'object' && !Array.isArray(r)) {
                return { template_row: r.template_row ?? null, origin: r.origin || 'manual' };
            }
            return { template_row: null, origin: 'manual' };
        });
    };

    const openBoq = (msgIndex) => {
        const msg = messages[msgIndex];
        if (!msg) return;
        if (Array.isArray(msg.boqColumns) && msg.boqColumns.length > 0) {
            const cols = orderColumns(msg.boqColumns);
            const rows = (msg.boqTable || []).map(r => {
                const obj = {};
                cols.forEach(c => { obj[c] = r?.[c] === null || r?.[c] === undefined ? '' : String(r[c]); });
                return obj;
            });
            setBoqColumns(cols); setBoqRows(rows); setBoqColumnLabels(msg.boqColumnLabels || null);
        } else {
            const { columns, rows, labels } = normalizeBoq(msg.boqTable);
            setBoqColumns(orderColumns(columns)); setBoqRows(rows); setBoqColumnLabels(labels || null);
        }
        setBoqTemplateId(null); setBoqTemplateType(msg.templateType || null); setBoqRawData(msg.boqRawData || null);
        setBoqRowsMeta(msg.boqRawData ? extractRowMeta(msg.boqRawData) : null); setBoqMsgIndex(msgIndex); setBoqOpen(true);
    };

    const closeBoq = () => {
        setBoqOpen(false); setBoqMsgIndex(null); setBoqTemplateId(null); setBoqColumnLabels(null);
        setBoqRawData(null); setBoqRowsMeta(null);
    };

    const openTemplate = (tpl) => {
        if (!tpl) return;
        setBoqColumns(orderColumns(tpl.columns || [])); setBoqRows(tpl.rows || []); setBoqColumnLabels(tpl.labels || null);
        setBoqTemplateType(tpl.type || null); setBoqRawData(tpl.raw || null); setBoqRowsMeta(tpl.raw ? extractRowMeta(tpl.raw) : null);
        setBoqMsgIndex(null); setBoqTemplateId(tpl.id); setBoqOpen(true);
    };

    const handleBoqCellChange = (rowIdx, col, value) => {
        setBoqRows(prev => {
            const next = [...prev];
            next[rowIdx] = { ...next[rowIdx], [col]: value };
            return next;
        });
    };

    const addBoqRow = () => {
        const blank = {};
        boqColumns.forEach(c => { blank[c] = ''; });
        setBoqRows(prev => [...prev, blank]);
        setBoqRowsMeta(prev => [...(prev || []), { template_row: null, origin: 'manual' }]);
    };

    const deleteBoqRow = (rowIdx) => {
        setBoqRows(prev => prev.filter((_, i) => i !== rowIdx));
        setBoqRowsMeta(prev => (prev || []).filter((_, i) => i !== rowIdx));
    };

    const saveBoq = async () => {
        if (boqMsgIndex === null) {
            if (boqTemplateId === null) return;
            setTemplates(prev => prev.map(template => (template.id === boqTemplateId ? { ...template, columns: boqColumns, rows: boqRows, labels: boqColumnLabels } : template)));
            closeBoq();
            return;
        }
        const msg = messages[boqMsgIndex];
        const boqId = msg?.boqId;

        if (boqId) {
            setBoqSaving(true);
            try {
                await axios.put(`${API_URL}/tendering/boq/${boqId}`, { columns: boqColumns, rows: boqRows }, { headers: { ...headers, 'Content-Type': 'application/json' } });
            } catch (err) {
                console.error('Save BOQ error:', err);
                alert('Could not save BOQ: ' + (err.response?.data?.error || err.message));
                setBoqSaving(false); return;
            } finally {
                setBoqSaving(false);
            }
        }
        setMessages(prev => {
            const base = [...prev];
            if (base[boqMsgIndex]) base[boqMsgIndex] = { ...base[boqMsgIndex], boqTable: boqRows, boqColumns };
            return base;
        });
        closeBoq();
    };

    const exportBoq = async () => {
        if (boqColumns.length === 0) return;
        setBoqExporting(true);
        try {
            const rawType = String(boqTemplateType || 'standard').toLowerCase();
            const boqType = rawType === 'client' ? 'client' : 'standard';

            let boqData;
            if (boqType === 'client' && boqRawData) {
                const rawColumns = Array.isArray(boqRawData.columns) ? boqRawData.columns : boqColumns;
                const rows = boqRows.map((r, i) => {
                    const meta = (boqRowsMeta && boqRowsMeta[i]) || { template_row: null, origin: 'manual' };
                    const out = {};
                    boqColumns.forEach(c => { out[c] = r[c] ?? ''; });
                    out.template_row = meta.template_row ?? null;
                    out.origin = meta.origin || 'manual';
                    return out;
                });
                boqData = { ...boqRawData, columns: rawColumns, rows };
            } else {
                const rows = boqRows.map(r => { const obj = {}; boqColumns.forEach(c => { obj[c] = r[c] ?? ''; }); return obj; });
                boqData = { rows };
            }

            const res = await axios.post(`${API_URL}/tendering/export_boq_temaplte`,
                { session_id: guid, boq_type: boqType, boq_data: boqData },
                { headers, responseType: 'blob', timeout: 300000 }
            );

            const ctype = res.headers?.['content-type'] || '';
            if (ctype.includes('application/json')) {
                const text = await res.data.text();
                let msg = text;
                try { msg = JSON.parse(text).message || JSON.parse(text).error || text; } catch (e) {}
                alert('Export failed: ' + msg); return;
            }

            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'BOQ_Schedule.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            console.error('Export error:', err);
            let msg = err.response?.data?.error || err.message;
            if (err.response?.data instanceof Blob) {
                try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (e) {}
            }
            alert('Export failed: ' + msg);
        } finally {
            setBoqExporting(false);
        }
    };

    if (loading) return <Loading />;

    const currentStatusStyle = STATUS_STYLE[session?.work_status || 'New'];

    // Custom Input Style
    const friendlyInputStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            bgcolor: '#F1F2F6',
            transition: 'all 0.3s ease',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: '#DFE4EA' },
            '&.Mui-focused fieldset': { borderColor: '#1e3c72', borderWidth: '2px' },
            '&.Mui-focused': { bgcolor: '#FFFFFF', boxShadow: '0 4px 20px rgba(30, 60, 114, 0.1)' }
        }
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', bgcolor: '#F8F9FE', height: 'calc(100vh - 4rem)', overflowY: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: '"Nunito", "Segoe UI", sans-serif' }}>
            
            {/* Colorful & Friendly Header Banner */}
            <Box sx={{ 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                pt: { xs: 4, md: 5 }, pb: { xs: 8, md: 9 }, px: { xs: 3, md: 5 },
                borderRadius: '0 0 40px 40px', mb: -5,
                boxShadow: '0 15px 35px rgba(30, 60, 114, 0.2)'
            }}>
                <Button
                    startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                    onClick={() => navigate('/tendering')}
                    sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, pl: 0, fontWeight: 800, textTransform: 'none', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-5px)' }, transition: 'all 0.3s' }}
                >
                    Back to Tenders
                </Button>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '16px', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                            <SmartToyRoundedIcon sx={{ fontSize: 30 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h4" fontWeight="900" sx={{ color: 'white', mb: 0.5, textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                                {passedSessionName || session?.session_name || session?.name || 'Untitled Tender'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', px: 1.5, py: 0.5, borderRadius: '50px' }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: currentStatusStyle.color, mr: 1, boxShadow: `0 0 8px ${currentStatusStyle.color}` }} />
                                    <Select
                                        value={session?.work_status || 'New'}
                                        onChange={(e) => handleWorkStatusChange(e.target.value)}
                                        variant="standard" disableUnderline
                                        sx={{ color: 'white', fontWeight: 800, fontSize: '0.75rem', '& .MuiSelect-select': { py: 0, pr: '16px !important' }, '& .MuiSvgIcon-root': { color: 'white', fontSize: 16 } }}
                                    >
                                        {WORK_STATUSES.map(st => <MenuItem key={st} value={st} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{st}</MenuItem>)}
                                    </Select>
                                </Box>
                                <Chip label={guid.substring(0, 8)} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, backdropFilter: 'blur(10px)' }} />
                                {session?.country && <Chip icon={<PublicRoundedIcon sx={{ color: '#fff !important', fontSize: '14px !important' }}/>} label={session.country} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, backdropFilter: 'blur(10px)' }} />}
                                {session?.division && <Chip icon={<BusinessRoundedIcon sx={{ color: '#fff !important', fontSize: '14px !important' }}/>} label={session.division} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, backdropFilter: 'blur(10px)' }} />}
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* File Badges */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            onClick={(e) => openFilesPopover(e, 'all')}
                            sx={{ minWidth: 0, textTransform: 'none', px: 2, py: 0.8, borderRadius: '50px', fontWeight: 800, fontSize: '0.8rem', color: fileFilter === 'all' ? '#1e3c72' : 'white', bgcolor: fileFilter === 'all' ? 'white' : 'rgba(255,255,255,0.2)', boxShadow: fileFilter === 'all' ? '0 5px 15px rgba(0,0,0,0.1)' : 'none', '&:hover': { bgcolor: 'white', color: '#1e3c72' }, transition: 'all 0.3s' }}
                        >
                            Files ({session?.file_count || 0})
                        </Button>
                        {Object.entries(TYPE_META).map(([k, m]) => {
                            const count = session?.file_types ? session.file_types[k] : 0;
                            if (!count) return null;
                            return (
                                <Button
                                    key={k} onClick={(e) => openFilesPopover(e, k)}
                                    sx={{ minWidth: 0, textTransform: 'none', px: 2, py: 0.8, borderRadius: '50px', fontWeight: 800, fontSize: '0.8rem', color: fileFilter === k ? m.color : 'white', bgcolor: fileFilter === k ? 'white' : 'rgba(255,255,255,0.2)', boxShadow: fileFilter === k ? '0 5px 15px rgba(0,0,0,0.1)' : 'none', '&:hover': { bgcolor: 'white', color: m.color }, transition: 'all 0.3s' }}
                                >
                                    {m.label} ({count})
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Main Layout Area */}
            <Box sx={{ display: 'flex', gap: 3, px: { xs: 3, md: 5 }, position: 'relative', zIndex: 2, minHeight: 0, flex: 1, flexDirection: { xs: 'column', lg: 'row' } }}>
                
                {/* Sidebar (Templates) */}
                <Paper elevation={0} sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: '24px', overflow: 'hidden', bgcolor: '#FFFFFF', boxShadow: '0 10px 40px rgba(0,0,0,0.05)', border: '1px solid #F1F2F6' }}>
                    <Box sx={{ px: 3, py: 2, bgcolor: '#F8F9FE', borderBottom: '2px dashed #F1F2F6' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2F3542', textTransform: 'uppercase', letterSpacing: 1 }}>Generated BOQs</Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {templates.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}>
                                <TableChartRoundedIcon sx={{ fontSize: 40, color: '#A5B1C2', mb: 1 }} />
                                <Typography variant="caption" color="#747D8C" fontWeight={700} display="block">No templates yet.<br/>Ask the AI to generate one!</Typography>
                            </Box>
                        ) : (
                            templates.map(tpl => (
                                <Button
                                    key={tpl.id} fullWidth onClick={() => openTemplate(tpl)}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 2, py: 1.5, borderRadius: '16px', color: '#2F3542', bgcolor: '#F8F9FE', border: '1px solid transparent', '&:hover': { bgcolor: '#e8eef7', borderColor: '#1e3c72' }, transition: 'all 0.3s' }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                        <Box sx={{ width: 36, height: 36, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: tpl.type === 'client' ? '#E8FAEE' : '#e8eef7', color: tpl.type === 'client' ? '#2ED573' : '#1e3c72' }}>
                                            <TableChartRoundedIcon sx={{ fontSize: 20 }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2F3542', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                Template #{tpl.id}
                                            </Typography>
                                            <Typography variant="caption" color="#A5B1C2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                                                {tpl.type} • {tpl.rows.length} rows
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Button>
                            ))
                        )}
                    </Box>
                </Paper>

                {/* Chat Area */}
                <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'white', boxShadow: '0 10px 40px rgba(0,0,0,0.05)', border: '1px solid #F1F2F6' }}>
                    
                    {/* Messages Container */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {messages.map((m, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                                {m.sender !== 'user' && (
                                    <Avatar sx={{ bgcolor: '#e8eef7', color: '#1e3c72', width: 40, height: 40, mr: 2, borderRadius: '12px' }}>
                                        <SmartToyRoundedIcon sx={{ fontSize: 22 }} />
                                    </Avatar>
                                )}
                                <Box sx={{ maxWidth: '80%' }}>
                                    <Box sx={{
                                        p: 2.5,
                                        borderRadius: '24px',
                                        borderTopLeftRadius: m.sender === 'bot' ? '4px' : '24px',
                                        borderTopRightRadius: m.sender === 'user' ? '4px' : '24px',
                                        bgcolor: m.sender === 'user' ? '#1e3c72' : '#F8F9FE',
                                        color: m.sender === 'user' ? 'white' : '#2F3542',
                                        background: m.sender === 'user' ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' : '#F8F9FE',
                                        border: m.sender === 'user' ? 'none' : '1px solid #F1F2F6',
                                        boxShadow: m.sender === 'user' ? '0 10px 25px rgba(30, 60, 114, 0.2)' : 'none',
                                        whiteSpace: m.sender === 'user' ? 'pre-wrap' : 'normal',
                                        wordBreak: 'break-word',
                                        overflowX: 'auto',
                                        '& p': { m: 0, mb: 1, '&:last-of-type': { mb: 0 }, fontSize: '0.95rem' },
                                        '& strong, & b': { fontWeight: 800, color: m.sender === 'user' ? 'white' : '#2F3542' },
                                        '& h1, & h2, & h3, & h4': { fontSize: '1.1rem', fontWeight: 800, m: 0, mt: 2, mb: 1, color: m.sender === 'user' ? 'white' : '#2F3542' },
                                        '& pre': { bgcolor: m.sender === 'user' ? 'rgba(0,0,0,0.1)' : '#FFFFFF', p: 2, borderRadius: '16px', overflowX: 'auto', my: 1, fontSize: '0.85rem', border: m.sender === 'user' ? 'none' : '1px solid #DFE4EA' },
                                        '& code': { fontFamily: 'monospace', bgcolor: m.sender === 'user' ? 'rgba(0,0,0,0.1)' : '#FFFFFF', px: 1, py: 0.3, borderRadius: '6px', fontSize: '0.85rem', border: m.sender === 'user' ? 'none' : '1px solid #DFE4EA' },
                                        '& a': { color: m.sender === 'user' ? '#DBEAFE' : '#2a5298', fontWeight: 700 },
                                        '& ul, & ol': { m: 0, pl: 3, mb: 1 },
                                        '& li': { mb: 0.5 },
                                        '& table': { borderCollapse: 'collapse', width: '100%', my: 2, fontSize: '0.85rem', borderRadius: '12px', overflow: 'hidden' },
                                        '& th, & td': { border: `1px solid ${m.sender === 'user' ? 'rgba(255,255,255,0.2)' : '#DFE4EA'}`, px: 1.5, py: 1, textAlign: 'left' },
                                        '& th': { bgcolor: m.sender === 'user' ? 'rgba(0,0,0,0.1)' : '#F1F2F6', fontWeight: 800 },
                                    }}>
                                        {m.isLoader ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <CircularProgress size={18} sx={{ color: '#1e3c72' }} />
                                                <Typography variant="body1" fontWeight={600}>{m.text}</Typography>
                                            </Box>
                                        ) : m.sender === 'user' ? (
                                            <Typography variant="body1" fontWeight={600}>{m.text}</Typography>
                                        ) : (
                                            <Typography variant="body1" component="div" dangerouslySetInnerHTML={{ __html: marked.parse(m.text || '') }} />
                                        )}
                                        
                                        {m.hasBoq && !m.isLoader && (
                                            <Box sx={{ mt: 2, pt: 2, borderTop: '2px dashed #DFE4EA', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<TableChartRoundedIcon />}
                                                    onClick={() => openBoq(i)}
                                                    sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 800, bgcolor: '#1e3c72', color: 'white', px: 3, boxShadow: '0 8px 20px rgba(30, 60, 114, 0.25)', '&:hover': { bgcolor: '#2a5298' } }}
                                                >
                                                    View BOQ ({Array.isArray(m.boqTable) ? m.boqTable.length : 0})
                                                </Button>
                                                {m.templateType && (
                                                    <Chip size="small" label={`${m.templateType} Template`} sx={{ height: 26, fontSize: '0.75rem', fontWeight: 800, bgcolor: '#FFFFFF', color: '#747D8C', border: '1px solid #DFE4EA' }} />
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                {m.sender === 'user' && (
                                    <Avatar sx={{ bgcolor: '#e8eef7', color: '#1e3c72', width: 40, height: 40, ml: 2, borderRadius: '12px' }}>
                                        <PersonRoundedIcon sx={{ fontSize: 22 }} />
                                    </Avatar>
                                )}
                            </Box>
                        ))}
                        <div ref={chatEndRef} />
                    </Box>

                    {/* Chat Input */}
                    <Box sx={{ p: 3, bgcolor: '#FFFFFF', borderTop: '1px solid #F1F2F6' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#F8F9FE', p: 1, borderRadius: '50px', border: '1px solid #F1F2F6', transition: 'all 0.3s', '&:focus-within': { borderColor: '#1e3c72', bgcolor: '#FFFFFF', boxShadow: '0 5px 20px rgba(30, 60, 114, 0.1)' } }}>
                            <TextField
                                fullWidth
                                placeholder="Type your message to analyze documents..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                                disabled={chatLoading}
                                variant="standard"
                                InputProps={{ disableUnderline: true, sx: { color: '#2F3542', fontWeight: 600, px: 2 } }}
                            />
                            <IconButton 
                                onClick={handleSendMessage} 
                                disabled={chatLoading || !chatInput.trim()} 
                                sx={{ bgcolor: '#1e3c72', color: '#fff', width: 44, height: 44, borderRadius: '50%', '&:hover': { bgcolor: '#2a5298', transform: 'scale(1.05)' }, '&.Mui-disabled': { bgcolor: '#DFE4EA', color: '#A5B1C2' }, transition: 'all 0.2s' }}
                            >
                                <SendRoundedIcon sx={{ fontSize: 20, ml: 0.5 }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Custom File Popover */}
            <Popover
                open={Boolean(filesAnchorEl)} anchorEl={filesAnchorEl} onClose={closeFilesPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { borderRadius: '24px', mt: 1, width: 360, border: '1px solid #F1F2F6', boxShadow: '0 15px 40px rgba(0,0,0,0.08)' } }}
            >
                <Box sx={{ p: 2, bgcolor: '#F8F9FE', borderBottom: '2px dashed #F1F2F6' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#2F3542' }}>
                        {fileFilter === 'all' ? 'All Files' : `${TYPE_META[fileFilter]?.label || 'File'}s`} ({popoverFiles.length})
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: 360, overflowY: 'auto', p: 1.5 }}>
                    {popoverFiles.length > 0 ? (
                        <Stack spacing={1}>
                            {popoverFiles.map((f) => {
                                const meta = fileTypeMeta(f.file_type);
                                return (
                                    <Paper key={f.id} elevation={0} sx={{ p: 1.5, borderRadius: '16px', border: '1px solid #F1F2F6', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'white', '&:hover': { borderColor: meta.color, boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }, transition: 'all 0.2s' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '10px', bgcolor: meta.bg, color: meta.color }}>
                                            {meta.icon}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={800} color="#2F3542" noWrap>{f.file_name}</Typography>
                                            <Typography variant="caption" fontWeight={700} color="#A5B1C2">{meta.label}</Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" onClick={() => handlePreview(f)} sx={{ bgcolor: '#F1F2F6', color: '#747D8C', '&:hover': { bgcolor: '#1e3c72', color: 'white' } }}><VisibilityRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                                            <IconButton size="small" onClick={() => handleDownload(f)} sx={{ bgcolor: '#F1F2F6', color: '#747D8C', '&:hover': { bgcolor: '#2ED573', color: 'white' } }}><DownloadRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="#A5B1C2" fontWeight={700} sx={{ p: 2, textAlign: 'center' }}>No files found.</Typography>
                    )}
                </Box>
            </Popover>

            {/* Custom Global Dialog Styles */}
            <style>{`
                .MuiDialog-paper { border-radius: 24px !important; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; overflow: hidden !important; }
                .MuiBackdrop-root { background-color: rgba(47, 53, 66, 0.6) !important; backdrop-filter: blur(5px); }
            `}</style>

            {/* Modern BOQ Editable Dialog (Light Theme) */}
            <Dialog open={boqOpen} onClose={closeBoq} maxWidth="xl" fullWidth TransitionComponent={Fade} PaperProps={{ sx: { bgcolor: '#FFFFFF', color: '#1e293b', border: '1px solid #E2E8F0' } }}>
                <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: '#e8eef7', borderRadius: '10px' }}><TableChartRoundedIcon sx={{ color: '#1e3c72' }} /></Box>
                        <Box>
                            <Typography variant="h6" fontWeight="900" color="#1e293b">BOQ Table Editor</Typography>
                            {boqTemplateType && <Typography variant="caption" fontWeight="700" sx={{ color: '#2a5298', textTransform: 'uppercase', letterSpacing: 1 }}>{boqTemplateType} Template</Typography>}
                        </Box>
                    </Box>
                    <IconButton onClick={closeBoq} sx={{ color: '#6B7280', bgcolor: '#F4F6F8', '&:hover': { bgcolor: '#E2E8F0', color: '#1e293b' } }}><CloseRoundedIcon /></IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0, bgcolor: '#FFFFFF' }}>
                    {boqColumns.length === 0 ? (
                        <Box sx={{ py: 10, textAlign: 'center' }}><Typography variant="h6" fontWeight="800" color="#6B7280">No BOQ data found.</Typography></Box>
                    ) : (
                        <>
                            <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F4F6F8', borderBottom: '1px solid #E2E8F0' }}>
                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>All cells are editable. Changes will be saved to this BOQ.</Typography>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Button
                                        startIcon={<AddCircleRoundedIcon />}
                                        onClick={addBoqRow} disabled={boqColumns.length === 0}
                                        sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 800, bgcolor: '#E2E8F0', color: '#1e293b', px: 2, '&:hover': { bgcolor: '#CBD5E1' } }}
                                    >
                                        Add Row
                                    </Button>
                                    <Button
                                        startIcon={boqExporting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <FileDownloadRoundedIcon />}
                                        onClick={exportBoq} disabled={boqColumns.length === 0 || boqExporting}
                                        sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 800, bgcolor: '#1e3c72', color: '#FFFFFF', px: 3, boxShadow: '0 4px 15px rgba(30, 60, 114, 0.3)', '&:hover': { bgcolor: '#2a5298' } }}
                                    >
                                        {boqExporting ? 'Exporting...' : 'Export Excel'}
                                    </Button>
                                </Box>
                            </Box>
                            
                            <TableContainer sx={{ maxHeight: '60vh', p: 3, bgcolor: '#FFFFFF' }}>
                                <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                                    <TableHead>
                                        <TableRow>
                                            {boqColumns.map((c) => (
                                                <TableCell key={c} sx={{ fontWeight: 800, color: '#1e3c72', fontSize: '0.75rem', bgcolor: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 1, borderBottom: 'none', px: 1.5 }}>{boqColumnLabels?.[c] || c}</TableCell>
                                            ))}
                                            <TableCell align="center" sx={{ bgcolor: '#FFFFFF', borderBottom: 'none', width: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boqRows.map((row, rowIdx) => (
                                            <TableRow key={rowIdx} sx={{ '& td': { borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }, '& td:first-of-type': { borderLeft: '1px solid #E2E8F0', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' }, '& td:last-of-type': { borderRight: '1px solid #E2E8F0', borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }, transition: 'all 0.2s', '&:hover td': { bgcolor: '#EFF3F8', borderColor: '#CBD5E1' } }}>
                                                {boqColumns.map((c) => (
                                                    <TableCell key={c} sx={{ p: 1, minWidth: 120 }}>
                                                        <textarea
                                                            value={row[c] ?? ''}
                                                            onChange={(e) => handleBoqCellChange(rowIdx, c, e.target.value)}
                                                            rows={1}
                                                            style={{
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                resize: 'vertical',
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: 8,
                                                                background: '#FFFFFF',
                                                                color: '#1e293b',
                                                                fontWeight: 600,
                                                                fontSize: '0.85rem',
                                                                padding: 8,
                                                                fontFamily: 'inherit',
                                                                lineHeight: 1.4,
                                                                minHeight: 34,
                                                                outline: 'none',
                                                                transition: 'all 0.2s',
                                                            }}
                                                            onFocus={(e) => { e.currentTarget.style.border = '1px solid #1e3c72'; }}
                                                            onBlur={(e) => { e.currentTarget.style.border = '1px solid #E2E8F0'; }}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell align="center">
                                                    <Tooltip title="Delete row">
                                                        <IconButton size="small" onClick={() => deleteBoqRow(rowIdx)} sx={{ color: '#FF4757', bgcolor: 'rgba(255,71,87,0.1)', '&:hover': { bgcolor: '#FF4757', color: 'white' } }}>
                                                            <DeleteRoundedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {boqRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={boqColumns.length + 1} align="center" sx={{ py: 5, border: 'none !important', bgcolor: 'transparent !important' }}>
                                                    <Typography variant="body1" fontWeight="700" color="#6B7280">Table is empty. Add a row to start typing.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#F4F6F8', borderTop: '1px solid #E2E8F0' }}>
                    <Typography variant="subtitle2" sx={{ mr: 'auto', color: '#475569', fontWeight: 700 }}>Total Rows: {boqRows.length}</Typography>
                    <Button onClick={closeBoq} sx={{ fontWeight: 800, color: '#6B7280', borderRadius: '50px', px: 3 }}>Discard</Button>
                    <Button
                        variant="contained"
                        startIcon={boqSaving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveRoundedIcon />}
                        onClick={saveBoq} disabled={boqSaving}
                        sx={{ borderRadius: '50px', px: 4, fontWeight: 800, textTransform: 'none', bgcolor: '#1e3c72', color: 'white', boxShadow: '0 4px 15px rgba(30, 60, 114, 0.3)', '&:hover': { bgcolor: '#2a5298' } }}
                    >
                        {boqSaving ? 'Saving...' : 'Save Table'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Preview Dialog */}
            <Dialog open={Boolean(previewFile)} onClose={closePreview} maxWidth="lg" fullWidth TransitionComponent={Fade}>
                <Box sx={{ background: '#2F3542', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
                    <Typography variant="h6" fontWeight="800">{previewFile?.file_name || 'Document Preview'}</Typography>
                    <IconButton onClick={closePreview} sx={{ color: 'white' }}><CloseRoundedIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0, height: '85vh', bgcolor: '#F1F2F6' }}>
                    {previewUrl ? <iframe src={previewUrl} title="preview" style={{ width: '100%', height: '100%', border: 'none' }} /> : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default TenderingChat;